import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { KnowledgeMap } from '../KnowledgeMap';
import { StatusPill } from '../../ui/StatusPill';
import { activity, reviewQueue } from '../../../data/sampleResearch';
interface HeroOverviewProps {
  onStart: () => void;
}
export function HeroOverview({
  onStart
}: HeroOverviewProps) {
  return <div className="flex h-full flex-col gap-4" data-id="element-200" data-mp="32g6pwebqnfsm">
      <div className="flex items-end justify-between gap-4" data-id="element-201" data-mp="2zqop8s7pn47">
        <div data-id="element-202" data-mp="311pczqd2er3">
          <p className="text-[11px] text-ink-muted" data-id="element-203" data-mp="3v2ztd4giit6b">Tuesday · Cognitive Science Group</p>
          <p className="mt-0.5 text-lg font-semibold tracking-tight" data-id="element-204" data-mp="3v5dqdiur6fgi">Good morning, Amara</p>
        </div>
        <div className="hidden gap-5 text-right sm:flex" data-id="element-205" data-mp="3ld7z6bcbs22d">
          <div data-id="element-206" data-mp="3lcoqzmch8s1j">
            <p className="text-[11px] text-ink-muted" data-id="element-207" data-mp="3lb3wtxu2rb47">Sources linked</p>
            <p className="text-sm font-semibold tabular-nums" data-id="element-208" data-mp="mnzni1o9grco">248</p>
          </div>
          <div data-id="element-209" data-mp="hw7yvwoo0vp6">
            <p className="text-[11px] text-ink-muted" data-id="element-210" data-mp="hvpsw8c7b342">Concepts</p>
            <p className="text-sm font-semibold tabular-nums" data-id="element-211" data-mp="3h59hfd9ztlb0">1,284</p>
          </div>
        </div>
      </div>

      <div className="grid flex-1 gap-3 lg:grid-cols-[1.35fr_1fr]" data-id="element-212" data-mp="3m547jely26v0">
        <section className="flex flex-col rounded-2xl bg-white p-4 shadow-glow" aria-label="Review queue summary" data-id="element-213" data-mp="38dfv1a7s9gdi">
          <div className="flex items-baseline justify-between" data-id="element-214" data-mp="3ceja3ui0b8do">
            <p className="text-xs font-medium text-ink-muted" data-id="element-215" data-mp="3ccyj2euwi7kk">Awaiting your review</p>
            <span className="text-[11px] text-ink-faint" data-id="element-216" data-mp="2ydysh1ntvilr">Updated 2m ago</span>
          </div>
          <p className="mt-1 flex items-baseline gap-2" data-id="element-217" data-mp="33fo52lqp84by">
            <span className="text-4xl font-semibold tracking-display tabular-nums" data-id="element-218" data-mp="2pf3ozxc64mm5">12</span>
            <span className="text-sm text-ink-soft" data-id="element-219" data-mp="2tfnh2mhvx01f">proposed relationships</span>
          </p>
          <ul className="mt-3 divide-y divide-ink/[0.06]" data-id="element-220" data-mp="nni6fe6975du">
            {reviewQueue.map((item, i) => <li key={item.title} className={`flex items-center justify-between gap-3 py-2 ${i === 0 ? '' : 'opacity-80'}`} data-id="element-221" data-mp="rpc195trpf63" data-mpi={i}>
                <div className="min-w-0" data-id="element-222" data-mp="20mk0fmt9hfpu">
                  <p className="truncate text-[13px] font-medium" data-id="element-223" data-mp="20jec49sgihv6">{item.title}</p>
                  <p className="text-[11px] text-ink-muted" data-id="element-224" data-mp="1vlcu7s6roola">{item.meta}</p>
                </div>
                <StatusPill tone={item.tone} data-id="element-225" data-mp="1rlcdg0w741b2">{item.status}</StatusPill>
              </li>)}
          </ul>
          <button type="button" onClick={onStart} className="mt-auto inline-flex h-9 items-center justify-center gap-1.5 self-start rounded-full bg-accent px-4 text-[13px] font-medium text-white transition-colors duration-150 hover:bg-accent-ink" data-id="element-226" data-mp="1moc9bviwp8ml">
            Start review <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-227" data-mp="25kv7xqw33e4w" />
          </button>
        </section>

        <div className="hidden flex-col gap-3 lg:flex" data-id="element-228" data-mp="32zzettut4vfw">
          <section className="rounded-2xl bg-white/70 p-3 ring-1 ring-ink/[0.05]" aria-label="Knowledge map preview" data-id="element-229" data-mp="331k7fdxka4b4">
            <p className="px-1 text-xs font-medium text-ink-muted" data-id="element-230" data-mp="2y7oeipw5n7mt">Knowledge map</p>
            <KnowledgeMap className="mt-1 h-[120px] w-full" labels={false} data-id="element-231" data-mp="35mi6ywfrvshy" />
          </section>
          <section className="flex-1 rounded-2xl bg-white/70 p-3 ring-1 ring-ink/[0.05]" aria-label="Recent activity" data-id="element-232" data-mp="3ak0i8th9ijt4">
            <p className="px-1 text-xs font-medium text-ink-muted" data-id="element-233" data-mp="3fl7q0sjenizs">Activity</p>
            <ul className="mt-2 space-y-2" data-id="element-234" data-mp="3jlrqa4z9z2q5">
              {activity.map(a => <li key={a.what} className="flex gap-2 px-1 text-[12px] leading-snug" data-id="element-235" data-mp="3oj08g5kfb6re">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ink-faint" aria-hidden="true" data-id="element-236" data-mp="3thl03glm0i48" />
                  <span className="flex-1 text-ink-soft" data-id="element-237" data-mp="24guub39vvlz">
                    <span className="font-medium text-ink" data-id="element-238" data-mp="73jo6y9w4de4">{a.who}</span> {a.what}
                  </span>
                  <span className="text-ink-faint" data-id="element-239" data-mp="b5564y1a7hv6">{a.when}</span>
                </li>)}
            </ul>
          </section>
        </div>
      </div>
    </div>;
}